import { ccfRoutes } from '@app/ccf/ccf-routes';
import useAuth from '@app/auth/use-auth';
import { useCCF } from '@app/ccf/company-report/ccf-context';
import { Icons } from '@shared/components/content/icons';
import { Button, ButtonProps } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { generatePath, useNavigate } from 'react-router-dom';
import { Scope1And2CategorySlug } from '../config';

interface AddDataMenuProps {
  categorySlug: Scope1And2CategorySlug;
  /** Opens the manual single-entry dialog (the existing "Add data" form). */
  onAddManually: () => void;
  size?: ButtonProps['size'];
  className?: string;
}

/**
 * Combined "Add data" control for a Scope 1/2 category: a primary button that
 * opens a dropdown to either add a single entry manually or import a
 * spreadsheet. Mirrors the "New import" dropdown on the purchased-goods imports
 * page (`purchased-foods-imports-list-page-toolbar.tsx`).
 */
export default function AddDataMenu({
  categorySlug,
  onAddManually,
  size = 'sm',
  className,
}: AddDataMenuProps) {
  const { uuid: reportUuid } = useCCF();
  const navigate = useNavigate();
  const { user } = useAuth();

  // The spreadsheet importer is still staff-only; everyone else keeps the
  // original single "Add data" button that opens the manual entry form.
  if (!user?.isStaff) {
    return (
      <Button
        size={size}
        className={className}
        onClick={onAddManually}
        data-testid="add-data-button"
      >
        Add data
      </Button>
    );
  }

  return (
    // modal={false} prevents Radix from locking body pointer-events while the
    // menu closes; opening the manual-entry Dialog from a menu item otherwise
    // leaves `pointer-events: none` stuck on <body> after the dialog closes.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="flex items-center" asChild>
        <Button size={size} className={className} data-testid="add-data-button">
          Add data
          <Icons.chevronDown className="ml-1 size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={onAddManually}
          data-testid="add-form-menuitem"
        >
          <Icons.fileSpreadsheet className="size-4" />
          Form
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="import-spreadsheet-menuitem"
          onClick={() =>
            navigate(
              generatePath(ccfRoutes.SCOPE_1_2_IMPORT_PROCESS, {
                report_uuid: reportUuid,
                category_slug: categorySlug,
              })
            )
          }
        >
          <Icons.import className="size-4" />
          Import
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
